import React from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import axios from "axios";
import { Dialog, DialogTitle, DialogContent, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";
import { path_url } from "../../Config/config";
import { FaConciergeBell } from "react-icons/fa";

const serviceSchema = Yup.object().shape({
  subject: Yup.string().required("Subject is required"),
  duedate: Yup.string().required("Due date is required"),
  priority: Yup.string().required("Priority is required"),
  status: Yup.string().required("Status is required"),
  serviceRequestType: Yup.string().required("Request type is required"),
  notes: Yup.string(),
});

const PriorityOptions = [
  "High",
  "Medium",
  "Low",
  "Critical",
  "Normal",
  "Emergency",
  "Deferred",
];

const StatusOptions = [
  "Open",
  "InProgress",
  "Pending",
  "Completed",
  "Closed",
];

const TypeOptions = [
  { value: "TechnicalSupportRequest", label: "Technical Support" },
  { value: "ProductInformationRequest", label: "Product Information" },
  { value: "OrderStatusInquiry", label: "Order Status Inquiry" },
  { value: "BillingPaymentInquiry", label: "Billing & Payment Inquiry" },
  { value: "Complaints", label: "Complaints" },
  { value: "TroubleshootingAssistance", label: "Troubleshooting Assistance" },
  { value: "MaintenanceRepairRequest", label: "Maintenance & Repair" },
  { value: "FeedbackSuggestions", label: "Feedback & Suggestions" },
];

export default function CreateServiceRequestModal({
  open,
  onClose,
  leadId,
  customerId,
  vendorId,
  onServiceCreated,
}) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        style: {
          borderRadius: "12px",
          padding: "8px",
        },
      }}
    >
      <DialogTitle className="d-flex justify-content-between align-items-center pb-2 border-bottom">
        <div
          className="d-flex align-items-center gap-2"
          style={{ color: "#17a2b8", fontWeight: 600 }}
        >
          <FaConciergeBell style={{ fontSize: "1.25rem" }} />
          <span>Create Service Request</span>
        </div>
        <IconButton onClick={onClose} size="small" aria-label="close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent className="pt-4">
        <Formik
          initialValues={{
            subject: "",
            duedate: new Date().toISOString().split("T")[0],
            priority: "Medium",
            status: "Open",
            serviceRequestType: "TechnicalSupportRequest",
            notes: "",
          }}
          validationSchema={serviceSchema}
          onSubmit={async (values, { setSubmitting, resetForm }) => {
            try {
              const token = localStorage.getItem("accessToken");
              const adminId = localStorage.getItem("adminId");
              const payload = {
                ...values,
                ...(leadId ? { leadid: leadId } : {}),
                ...(customerId ? { customerid: customerId } : {}),
                ...(vendorId ? { vendorid: vendorId } : {}),
                useradminid: adminId,
                usercid: adminId,
              };

              let response;
              try {
                response = await axios.post(`${path_url}/servicerequest`, payload, {
                  headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                  },
                });
              } catch (err) {
                // Fallback attempt to task or createtask endpoint if servicerequest endpoint returns 404
                if (err.response && err.response.status === 404) {
                  response = await axios.post(`${path_url}/task`, payload, {
                    headers: {
                      Authorization: `Bearer ${token}`,
                      "Content-Type": "application/json",
                    },
                  });
                } else {
                  throw err;
                }
              }

              if (
                response.data?.statusCodeValue === 200 ||
                response.status === 200
              ) {
                toast.success("Service Request created successfully!");
                const createdRecord = response.data?.body || payload;
                if (onServiceCreated) {
                  onServiceCreated(createdRecord);
                }
                resetForm();
                onClose();
              } else {
                toast.error(
                  response.data?.body || "Failed to create service request"
                );
              }
            } catch (error) {
              console.error("Service request creation error:", error);
              toast.error(
                error.response?.data?.message ||
                  error.message ||
                  "Failed to create service request"
              );
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            isSubmitting,
          }) => (
            <Form onSubmit={handleSubmit} className="row g-3 mt-1">
              <div className="col-md-6 mb-3">
                <label className="form-label font-weight-bold">
                  Subject <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  name="subject"
                  className={`form-control ${
                    touched.subject && errors.subject ? "is-invalid" : ""
                  }`}
                  placeholder="Service request summary"
                  value={values.subject}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                {touched.subject && errors.subject && (
                  <div className="invalid-feedback">{errors.subject}</div>
                )}
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label font-weight-bold">
                  Target Due Date <span className="text-danger">*</span>
                </label>
                <input
                  type="date"
                  name="duedate"
                  className={`form-control ${
                    touched.duedate && errors.duedate ? "is-invalid" : ""
                  }`}
                  value={values.duedate}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                {touched.duedate && errors.duedate && (
                  <div className="invalid-feedback">{errors.duedate}</div>
                )}
              </div>

              <div className="col-md-4 mb-3">
                <label className="form-label font-weight-bold">
                  Request Type <span className="text-danger">*</span>
                </label>
                <select
                  name="serviceRequestType"
                  className={`form-select ${
                    touched.serviceRequestType && errors.serviceRequestType
                      ? "is-invalid"
                      : ""
                  }`}
                  value={values.serviceRequestType}
                  onChange={handleChange}
                  onBlur={handleBlur}
                >
                  {TypeOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                {touched.serviceRequestType && errors.serviceRequestType && (
                  <div className="invalid-feedback">
                    {errors.serviceRequestType}
                  </div>
                )}
              </div>

              <div className="col-md-4 mb-3">
                <label className="form-label font-weight-bold">
                  Priority <span className="text-danger">*</span>
                </label>
                <select
                  name="priority"
                  className={`form-select ${
                    touched.priority && errors.priority ? "is-invalid" : ""
                  }`}
                  value={values.priority}
                  onChange={handleChange}
                  onBlur={handleBlur}
                >
                  {PriorityOptions.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
                {touched.priority && errors.priority && (
                  <div className="invalid-feedback">{errors.priority}</div>
                )}
              </div>

              <div className="col-md-4 mb-3">
                <label className="form-label font-weight-bold">
                  Status <span className="text-danger">*</span>
                </label>
                <select
                  name="status"
                  className={`form-select ${
                    touched.status && errors.status ? "is-invalid" : ""
                  }`}
                  value={values.status}
                  onChange={handleChange}
                  onBlur={handleBlur}
                >
                  {StatusOptions.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
                {touched.status && errors.status && (
                  <div className="invalid-feedback">{errors.status}</div>
                )}
              </div>

              <div className="col-md-12 mb-3">
                <label className="form-label font-weight-bold">Notes</label>
                <textarea
                  name="notes"
                  rows={3}
                  className="form-control"
                  placeholder="Detailed description of service request..."
                  value={values.notes}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
              </div>

              <div className="col-12 d-flex justify-content-end gap-2 pt-3 border-top">
                <button
                  type="button"
                  className="btn btn-secondary px-4"
                  onClick={onClose}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-info text-white px-4"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Saving..." : "Create Request"}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
}
